import React from 'react';
import { motion } from 'motion/react';
import {
  Sparkles, TrendingUp, Award, Users, Trophy, Shield, Home,
  Gamepad2, Puzzle, Languages, Headphones, BookOpen, BarChart,
  Notebook, Smile, TabletSmartphone, Clock, Globe, MonitorPlay
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { features } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const iconMap = {
  Sparkles,
  TrendingUp,
  Award,
  Users,
  Trophy,
  Shield,
  Gamepad2,
  Puzzle,
  Languages,
  Headphones,
  BookOpen,
  BarChart,
  Notebook,
  Smile,
  TabletSmartphone,
  Clock,
  Globe,
  MonitorPlay
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const Features = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Powerful Features for <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Joyful Learning</span>
            </h1>
            <p className="text-xl text-orange-600 max-w-3xl mx-auto mb-8">
              Goodikids is packed with features designed to make learning engaging, trackable, and rewarding for kids aged 5-18.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                >
                  <Card className="p-8 h-full border-2 border-orange-200 shadow-lg hover:shadow-2xl hover:border-orange-500 transition-all duration-300">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-2xl flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">{feature.title}</h3>
                    <p className="text-gray-700 font-medium leading-relaxed">{feature.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-orange-500 via-yellow-500 to-green-500">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h3 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h3>
            <p className="text-xl text-white/90 mb-8">Download Goodikids today and unlock all these amazing features!</p>
            <Link to="/">
              <Button className="bg-white text-orange-500 hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold">
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Features;